// src/apsales/Company/AddEditCompany/index.tsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";

type Mode = "add" | "edit";

interface Props {
    /** Optional override. If not provided, will auto-detect from route param `id` */
    mode?: Mode;
}

interface State {
    id: string;
    name: string;
}

interface FormData {
    businessName: string;
    emailDomain: string;
    address: string;
    city: string;
    stateId: string;
    logoUrl: string;
    displayName: string;
    website: string;
    billingAccId: string;
    genericCoEmail: string;
    phoneNo: string;
    picName: string;
    picContact: string;
}

const EMPTY_FORM: FormData = {
    businessName: "",
    emailDomain: "",
    address: "",
    city: "",
    stateId: "",
    logoUrl: "",
    displayName: "",
    website: "",
    billingAccId: "",
    genericCoEmail: "",
    phoneNo: "",
    picName: "",
    picContact: "",
};

export default function AddEditCompany({ mode: modeProp }: Props) {
    const { id } = useParams(); // expect /companies/:id/edit for edit
    const mode: Mode = useMemo(() => {
        if (modeProp) return modeProp;
        return id ? "edit" : "add";
    }, [modeProp, id]);

    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [states, setStates] = useState<State[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(mode === "edit"); // load when editing
    const navigate = useNavigate();

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Load States (shared for add & edit)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const res = await axios.get(
                    `${apiUrl}/states`,
                    { headers: { Authorization: `Bearer ${getToken()}` } }
                );
                if (mounted) setStates(res.data?.data || []);
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // If edit: fetch company and fill the form — still *exact same* UI
    useEffect(() => {
        if (mode !== "edit" || !id) return;
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const res = await axios.get(
                    `${apiUrl}/companies/${id}`,
                    { headers: { Authorization: `Bearer ${getToken()}` } }
                );

                // Map API -> form fields (adjust keys only if your API differs)
                const c = res.data.data || res.data;
                const filled: FormData = {
                    businessName: c.businessName ?? "",
                    emailDomain: c.emailDomain ?? "",
                    address: c.address ?? "",
                    city: c.city ?? "",
                    stateId: c.stateId ?? "",
                    logoUrl: c.logoUrl ?? "",
                    displayName: c.displayName ?? "",
                    website: c.website ?? "",
                    billingAccId: c.billingAccId ?? c.billingAccountId ?? c.billing_acc_id ?? "",
                    genericCoEmail: c.genericCoEmail ?? "",
                    phoneNo: c.phoneNo ?? "",
                    picName: c.picName ?? "",
                    picContact: c.picContact ?? "",
                };
                if (mounted) setFormData(filled);
            } catch (err: any) {
                setError(err?.response?.data?.message || "Failed to load company.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [mode, id]);

    const handleSubmit = async () => {
        try {
            setError("");
            const token = getToken();
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            if (mode === "edit" && id) {
                await axios.put(
                    `${apiUrl}/companies/${id}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await axios.post(
                    `${apiUrl}/companies`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            navigate("/clients");
        } catch (err: any) {
            setError(err?.response?.data?.message || "Submission failed.");
        }
    };

    // ---------- UI (IDENTICAL layout & classes to your Add Company page) ----------
    return (
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
            <div className="col-span-12 sm:col-span-10 sm:col-start-2">
                <div className="flex flex-col lg:items-center lg:flex-row gap-y-2">
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group active",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    >
                        <div className="flex items-center">
                            <div className="bg-white border rounded-full group-[.mode--light]:!bg-transparent group-[.active]:bg-primary group-[.active]:text-white group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-white/[0.25] [.group.mode--light_.group.active_&]:!bg-white/[0.12] [.group.mode--light_.group.active_&]:!border-white/[0.15] dark:[.group.mode--light_.group.active_&]:!bg-darkmode-800/30 dark:[.group.mode--light_.group.active_&]:!border-white/10 dark:group-[.mode--light]:!border-white/10 dark:bg-transparent">
                                <div className="flex items-center justify-center w-10 h-10">
                                    1
                                </div>
                            </div>
                            <div className="ml-3.5 group-[.mode--light]:!text-slate-300 font-medium whitespace-nowrap text-slate-500 group-[.active]:text-current [.group.mode--light_.group.active_&]:!text-slate-100">
                                {mode === "edit" ? " Edit " : ""}
                                Company Information                                
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    />
                </div>

                <div className="mt-7">
                    <div className="flex flex-col box box--stacked">
                        <div className="p-7">
                            {/* === Logo block (unchanged layout) === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Company Logo</div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Upload company logo.
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex items-center">
                                        <div className="relative flex items-center justify-center w-24 h-24 border rounded-full bg-primary/5 border-primary/10">
                                            <Lucide
                                                icon="User"
                                                className="w-[65%] h-[65%] fill-slate-300/70 -mt-1.5 stroke-[0.5] stroke-slate-400/50"
                                            />
                                            <a
                                                href=""
                                                className="absolute bottom-0 right-0 flex items-center justify-center rounded-full box w-7 h-7"
                                                onClick={(e) => e.preventDefault()}
                                            >
                                                <Lucide
                                                    icon="Pencil"
                                                    className="w-3.5 h-3.5 stroke-[1.3] text-slate-500"
                                                />
                                            </a>
                                        </div>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="pl-3.5 pr-4 mr-2 h-8 ml-8"
                                        >
                                            <Lucide
                                                icon="Trash2"
                                                className="w-3.5 h-3.5 mr-1.5 stroke-[1.3]"
                                            />{" "}
                                            Remove
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* === Company Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Company Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Enter legal business name
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex flex-col items-center md:flex-row">
                                        <FormInput
                                            type="text"
                                            name="businessName"
                                            required
                                            placeholder="e.g., ABC Sdn Bhd"
                                            value={formData.businessName}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* === Email Domain === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Email Domain</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Company email domain (e.g., abc.com.my)
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex flex-col items-center md:flex-row">
                                        <FormInput
                                            type="text"
                                            name="emailDomain"
                                            required
                                            placeholder="e.g., abc.com.my"
                                            value={formData.emailDomain}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* === Display Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Display Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex flex-col items-center md:flex-row">
                                        <FormInput
                                            type="text"
                                            name="displayName"
                                            placeholder="e.g., ABC"
                                            value={formData.displayName}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* === Address === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Address</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Office Address
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="address"
                                        required
                                        placeholder="e.g., 10 Jalan Damai 1/2, Taman Damai"
                                        value={formData.address}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === City === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">City</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="city"
                                        required
                                        placeholder="e.g., Puchong"
                                        value={formData.city}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === State === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">State</div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormSelect
                                        name="stateId"
                                        value={formData.stateId}
                                        onChange={handleChange}
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">Select a state</option>
                                        {states.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </FormSelect>
                                </div>
                            </div>

                            {/* === Phone === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Office Phone No</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="phoneNo"
                                        required
                                        placeholder="e.g., 03 7887 8877"
                                        value={formData.phoneNo}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === Email === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Email</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Please provide a valid email address.
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="genericCoEmail"
                                        required
                                        placeholder="e.g., hello@abc.com.my"
                                        value={formData.genericCoEmail}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === Website === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Website</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Business website.
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="website"
                                        placeholder="e.g., www.abc.com.my"
                                        value={formData.website}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === PIC Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">PIC Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="picName"
                                        required
                                        placeholder="e.g., Johny"
                                        value={formData.picName}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === PIC Contact === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">PIC Contact No</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="picContact"
                                        required
                                        placeholder="e.g., 0123456789"
                                        value={formData.picContact}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* === Billing ID === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Account Billing ID</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="billingAccId"
                                        required
                                        placeholder="e.g., A-01"
                                        value={formData.billingAccId}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="mt-6 text-danger text-sm">{error}</div>
                            )}
                        </div>

                        <div className="flex py-5 border-t md:justify-end px-7 border-slate-200/80">
                            <Button
                                variant="outline-primary"
                                className="w-full px-10 md:w-auto border-primary/50"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                <Lucide icon="Pocket" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
